import type { CSSObject } from '@mui/material/styles';
import { makePalette, palette } from './palette';

/**
 * The one focus indicator, shared by every web product.
 *
 * WCAG 2.1 AA needs a *visible* focus indicator (2.4.7) whose contrast against
 * the adjacent background is at least 3:1 (1.4.11). Neither the gov/admin theme
 * nor the web theme defined one before this pass, so every custom
 * `component="button"` Box, link and input fell back to the browser default —
 * which is invisible on the deep-teal chrome.
 *
 * The ring is deliberately two-tone: the portal's `accent` as the outline
 * (>= 4.6:1 on white, cream, cream2, parch and its own tint) sitting inside an
 * `accentLight` halo (>= 4.9:1 on the portal's rail). Whatever surface the
 * control is on, one of the two rings clears 3:1 — so the same declaration
 * works on the light content area and on the dark navigation rail without
 * per-surface overrides. `contrast.spec.ts` gates both floors for all three
 * accent families.
 */
export function makeFocusRing(p: ReturnType<typeof makePalette>): CSSObject {
  return {
    outline: `2px solid ${p.border.focus}`,
    outlineOffset: '2px',
    boxShadow: `0 0 0 5px ${p.border.focusOnDark}`,
  };
}

/**
 * The default (Government Portal) focus ring, for components that style focus
 * themselves outside a portal theme. Inside a theme the ring comes from
 * `MuiCssBaseline`, which is built per portal.
 */
export const focusRing: CSSObject = makeFocusRing(palette);

/**
 * Baseline `:focus-visible` rules for `MuiCssBaseline`.
 *
 * Applied to every focusable element rather than to a list of MUI components,
 * because most of the missing indicators were on hand-rolled `Box
 * component="button"` controls that no component override would have reached.
 * `:focus-visible` (not `:focus`) keeps the ring off mouse clicks.
 */
export function makeFocusVisibleBaseline(
  p: ReturnType<typeof makePalette>,
): Record<string, CSSObject> {
  return {
    // The `:where()` wrapper keeps specificity at 0 so any component-level focus
    // style still wins; it only has to beat the UA default.
    //
    // Not on the native element inside an MUI field (`.MuiInputBase-input`: the
    // `<input>`/`<textarea>` of a TextField, and the focusable div of a Select).
    // That field already draws its own focus indicator — the notched outline
    // thickens to 2px in the accent on `.Mui-focused` — so the baseline ring was
    // a second, inset box inside it, whose 5px halo also stuck out past the
    // field's right edge. The exclusion sits inside `:where()` so it adds no
    // specificity either.
    ':where(a, button, input, select, textarea, summary, [tabindex]):where(:not(.MuiInputBase-input)):focus-visible':
      makeFocusRing(p),
    // Elements that take programmatic focus for focus management (route change,
    // stage advance) should not paint a ring — the announcement is the feedback.
    ':where([tabindex="-1"]):focus': { outline: 'none', boxShadow: 'none' },
    ':where([tabindex="-1"]):focus-visible': { outline: 'none', boxShadow: 'none' },
  };
}

export const focusVisibleBaseline: Record<string, CSSObject> = makeFocusVisibleBaseline(palette);

/**
 * Visually hidden, but still read by assistive technology and still focusable.
 * Used by the skip link (which must appear on focus) and by live regions that
 * carry no visible text of their own.
 *
 * Lengths are written as explicit `px` strings because every consumer spreads
 * this into `sx`, where a bare `1` is not one pixel: the sizing transform reads
 * numbers <= 1 as fractions (`width: 1` -> `100%`) and the spacing transform
 * multiplies `margin: -1` by the 8px unit. That turned each hidden region into a
 * full-viewport block in the document flow — the empty gap under short pages.
 */
export const visuallyHidden: CSSObject = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
};
